import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../api';
import { AlternarDaBarra, BarraDeLista, FiltrosAtivos } from '../../BarraDeLista';
import { comTelefoneLegivel, plural } from '../../formato';
import { degrauDe, NIVEL_DO_RADAR } from '../../situacoes';
import { AtualizacaoPausada, useTempoReal } from '../../tempoReal';
import { Carregando, ErroDaApi, SeloDaEscada } from '../../ui';
import {
  FALTA_NO_RADAR, quemAgeNoRadar, regraDoRadar, tempoNoRadar, textoDe, type LimitesDoRadar, type LinhaDoRadar,
} from './crm';
import './radar.css';

/**
 * O Radar — onda 550 (Plano 2 · Comercial, Etapa 1, Tarefa 19; spec do
 * Comercial §8.6). `/comercial/radar`.
 *
 * O estado de agora, não um relatório: as conversas sem a nossa resposta ou
 * à espera do cliente, as propostas paradas sem próxima ação e as tarefas
 * vencidas, em três níveis — Crítico, Em risco e Em andamento —, do mais
 * antigo ao mais novo em cada um. Cada item diz o que falta, há quanto tempo
 * (horas úteis na conversa e na tarefa; dias na proposta) e quem deve agir;
 * o item inteiro é um alvo só (P-CEL-07) e abre a conversa ou a proposta.
 * A partir de 3 dias úteis, o tempo útil se diz em dias úteis (SP-a).
 *
 * A regra é do banco (`fn_radar`, 0510), e os limites vêm dos ajustes: a
 * frase do cabeçalho os diz como estão agora. «Meus» nasce ligado (o que é
 * meu: eu ajo, a conversa é minha, a proposta é minha ou indiquei); desligado,
 * tudo o que a pessoa vê. O Radar não mostra valor nem texto de mensagem.
 *
 * Ao vivo pela caixa e pelo funil (`comercial.caixa`, `comercial.funil`), e
 * de minuto em minuto, porque o nível anda com o relógio.
 *
 * D7 (Humberto, 01/11/2026): uma linha por oportunidade. As pendências dela —
 * o cliente esperando a resposta, o acompanhamento atrasado, a proposta
 * parada, o retorno combinado — vêm listadas dentro da linha, que tem o
 * nível, o tempo e o lugar da mais urgente; as contas do nível são de linhas.
 * A pendência sem oportunidade (ou de uma que a pessoa não vê) é uma linha
 * própria.
 */

const NIVEIS = ['critico', 'em_risco', 'em_andamento'] as const;

/** A ausência do nível dita por extenso (P-TXT-02). */
const NADA_NO_NIVEL: Record<string, string> = {
  critico: 'Nada crítico agora.',
  em_risco: 'Nada em risco agora.',
  em_andamento: 'Nada em andamento agora.',
};

const TIPO: Record<string, string> = { conversa: 'Conversa', proposta: 'Proposta', tarefa: 'Tarefa' };

const tempoDe = tempoNoRadar;
const quemDe = quemAgeNoRadar;

export function Radar() {
  const [busca, setBusca] = useSearchParams();
  const meus = busca.get('meus') !== '0';
  const [itens, setItens] = useState<LinhaDoRadar[] | null>(null);
  const [limites, setLimites] = useState<LimitesDoRadar | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const pedido = useRef(0);

  const buscar = useCallback(async () => {
    const n = ++pedido.current;
    try {
      const r = await api<{ linhas: LinhaDoRadar[]; limites: LimitesDoRadar }>(`/comercial/radar?meus=${meus ? 1 : 0}`);
      if (n !== pedido.current) return;
      setItens(r.linhas);
      setLimites(r.limites);
      setErro(null);
    } catch (e) {
      if (n === pedido.current) setErro(e);
    }
  }, [meus]);

  useEffect(() => { void buscar(); }, [buscar]);
  /* O nível anda com o relógio, mesmo sem evento: de minuto em minuto. */
  useEffect(() => {
    const id = window.setInterval(() => { void buscar(); }, 60_000);
    return () => window.clearInterval(id);
  }, [buscar]);

  const adiada = useRef<number | null>(null);
  const tempoReal = useTempoReal(['comercial.caixa', 'comercial.funil'], () => {
    if (adiada.current !== null) return;
    adiada.current = window.setTimeout(() => { adiada.current = null; void buscar(); }, 300);
  });
  useEffect(() => () => { if (adiada.current !== null) window.clearTimeout(adiada.current); }, []);

  const mudarMeus = (v: boolean) => {
    const b = new URLSearchParams(busca);
    if (v) b.delete('meus'); else b.set('meus', '0');
    setBusca(b, { replace: true });
  };

  return (
    <div className="radar-tela">
      <header className="cabeca cabeca-lista">
        <div className="cresce">
          <h1>Radar</h1>
          <p className="sub">
            O que está parado agora, do mais grave ao mais leve e, em cada nível, do mais antigo.
            {limites && ` ${regraDoRadar(limites)}`}
          </p>
        </div>
      </header>

      {/* Um filtro só: no celular ele fica à vista, e não atrás de «Filtros» (M-4 da revisão). */}
      <BarraDeLista rotulo="Filtros do Radar" emLinha ativos={meus ? 0 : 1} total={itens?.length}
        aoLimpar={() => setBusca(new URLSearchParams(), { replace: true })}>
        <AlternarDaBarra ligado={meus} aoMudar={mudarMeus}>Meus</AlternarDaBarra>
      </BarraDeLista>
      <FiltrosAtivos itens={[]} aoLimpar={() => undefined}
        contagem={itens ? `${itens.length === 0 ? 'Nenhuma linha' : plural(itens.length, 'linha', 'linhas')} no Radar${meus ? ', só as suas' : ''}` : undefined} />
      <AtualizacaoPausada estado={tempoReal} />

      {erro && !itens ? <ErroDaApi erro={erro} />
        : !itens ? <Carregando texto="Carregando o Radar" />
          : NIVEIS.map((nivel) => {
            const degrau = degrauDe(NIVEL_DO_RADAR, nivel);
            const doNivel = itens.filter((i) => i.nivel === nivel);
            const idTitulo = `radar-nivel-${nivel}`;
            return (
              <section key={nivel} className="radar-nivel" data-nivel={nivel} aria-labelledby={idTitulo}>
                <h2 id={idTitulo} className="radar-nivel-titulo">
                  {degrau.texto} <span className="radar-conta">{doNivel.length}</span>
                </h2>
                {doNivel.length === 0 ? (
                  <p className="radar-vazio">{NADA_NO_NIVEL[nivel]}</p>
                ) : (
                  <ul className="cartao radar-lista">
                    {doNivel.map((i) => (
                      <li key={i.oportunidadeId && i.pendencias.length > 1 ? `o-${i.oportunidadeId}` : `${i.tipo}-${i.referenciaId}`}>
                        <Link className="radar-item" to={i.link} data-id={i.referenciaId} data-tipo={i.tipo}
                          data-oportunidade={i.oportunidadeId ?? undefined} data-pendencias={i.pendencias.length}>
                          <SeloDaEscada degrau={degrau} />
                          <span className="radar-corpo">
                            <span className="radar-titulo">
                              <span className="radar-tipo">{TIPO[i.tipo] ?? i.tipo}</span> {comTelefoneLegivel(i.titulo)}
                            </span>
                            <span className="radar-falta">{textoDe(FALTA_NO_RADAR, i.falta)} · {tempoDe(i)}</span>
                            <span className="radar-quem">Quem age: {quemDe(i)}</span>
                            {i.pendencias.length > 1 && (
                              <span className="radar-pendencias" aria-label={`${i.pendencias.length} pendências nesta oportunidade`}>
                                {i.pendencias.slice(1).map((p) => (
                                  <span key={`${p.tipo}-${p.referenciaId}`} className="radar-pendencia" data-id={p.referenciaId}
                                    data-tipo={p.tipo} data-nivel={p.nivel}>
                                    Também: {TIPO[p.tipo] ?? p.tipo} · {textoDe(FALTA_NO_RADAR, p.falta)} · {tempoDe(p)}
                                  </span>
                                ))}
                              </span>
                            )}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
    </div>
  );
}
